import { DZ_COLOR_BLK_BLACK, DZ_COLOR_BLK_WHITE } from '@blk/daisy/core/styles';
import { AbstractRenderer, Alignment, BusinessComponent, BusinessComponentOptions, CellPanel, ColumnPanel, ImageRenderer, RowPanel, SquareRenderer, StaticParagraphComponent } from '@blk/simba';
import { FundStatusInteractionsConstants } from '@ddt-web/common';
import { InjectConfig, IqConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { ComponentData, TabData, TabUtil } from '@ddt-web/data-service';
import { FundHealthStatusCardData, FundStatsUtil, FundStatusChange, FundStatusInteractionGroupConfig, StatusChangeType, StatusColour } from '@ddt-web/fund-stats';
import { IconRenderer } from '@ddt-web/pdf-utils';

import { DisclosureComponent } from '../disclaimers/disclosure-component';
import { HeadingComponent, HeadingLevel } from '../heading/heading-component';
import { SvgImage } from '../helpers';
import { HighchartsSVGComponent } from '../highchart/highchart-svg-component';

export class FundHealthSummaryComponent extends BusinessComponent {
	@InjectConfig(IqConfigKey.ENABLE_FUND_STATUS_INTERACTIONS) public readonly enableStatusInteractions = false;
	@InjectConfig(SharedConfigKey.SUPPORTED_FUND_STATUS_INTERACTIONS) private readonly supportedFundStatusInteractions: FundStatusInteractionGroupConfig[];

	private readonly fundHealthStatusDescription: string = 'The following shows the areas in your book/portfolio that need attention in order of importance';
	private readonly verticalSeparatorHeightMultiplier: number = 30;
	private readonly borderColor: string = '#b5b5b5';
	private readonly horizontalSeparator: string = `
	<svg width="700px" height="1px" viewBox="0 0 700 1">
		<rect id="Rectangle" x="10" y="0" width="700px" height="0.5px" fill="#b5b5b5"/>
	</svg>`;

	constructor(name: string, public dataComponentId: string, public visible: boolean = true) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
	}

	public buildComponent(componentData: ComponentData): AbstractRenderer<SVGGElement>[] {
		const childArtefacts: AbstractRenderer<SVGGElement>[] = [];
		const statusCards: Array<FundHealthStatusCardData> = FundStatsUtil.createStatusCards(componentData);
		const isStatusChanges = TabUtil.isDataAvailable(componentData, 'fund.status.changes.individual');

		if (componentData.success && statusCards) {
			childArtefacts.push(new HeadingComponent('FUND_STATUS_SUMMARY_TITLE', 'Fund Health Status', HeadingLevel.H2));
			childArtefacts.push(new StaticParagraphComponent('FUND_STATUS_SUMMARY_DESCRIPTION', this.fundHealthStatusDescription, {}, { paddingBottom: 8 }, []));
			childArtefacts.push(new RowPanel(
				'fund-health-panel',
				[
					...this.createStatusCardsCells(statusCards),
					new CellPanel(
						[this.getChart(componentData, statusCards)],
						{ proportion: 25, useFullHeight: true },
						Alignment.Horizontal.Left
					)
				],
				{
					borderTop: 0.5,
					borderBottom: isStatusChanges ? 0 : 0.5,
					borderLeft: 0.5,
					borderRight: 0.5,
					borderBottomColor: this.borderColor,
					borderTopColor: this.borderColor,
					borderLeftColor: this.borderColor,
					borderRightColor: this.borderColor
				},
				Alignment.Horizontal.Left
			));

			if (isStatusChanges) {
				childArtefacts.push(new RowPanel(null, [
					new CellPanel([new ImageRenderer(this.horizontalSeparator)], { proportion: 100, useFullWidth: true }, Alignment.Horizontal.Center)
				], {
					borderLeft: 0.5,
					borderRight: 0.5,
					borderLeftColor: this.borderColor,
					borderRightColor: this.borderColor
				}));
				childArtefacts.push(new RowPanel(
					'fund-health-status-changes-panel',
					[
						new CellPanel(
							[this.getStatusChanges(componentData)],
							{ proportion: 25, useFullHeight: true },
							Alignment.Horizontal.Left
						),
						...this.createStatusChangesCells(componentData)

					],
					{
						paddingBottom: 10,
						borderBottom: 0.5,
						borderLeft: 0.5,
						borderRight: 0.5,
						borderBottomColor: this.borderColor,
						borderLeftColor: this.borderColor,
						borderRightColor: this.borderColor
					},
					Alignment.Horizontal.Left
				));
			}
			childArtefacts.push(new DisclosureComponent('PDF_FUND_STATUS_DISCLOSURE', 'PDF_FUND_STATUS_DISCLOSURE', {}, { paddingTop: 10 }));
		}

		return childArtefacts;
	}

	private getChart(data: ComponentData, statusCards: Array<FundHealthStatusCardData>): ColumnPanel {
		const customAttributes = {
			chart: {
				marginLeft: 30,
				marginTop: 6
			},
			yAxis: {
				reversedStacks: false,
				tickPositions: [0, 25, 50, 75, 100],
				gridLineColor: this.borderColor,
				margin: 0,
				labels: {
					formatter: function (): string {
						return `${this.value}%`;
					}
				}
			},
			plotOptions: {
				column: {
					pointWidth: 30,
					dataLabels: {
						enabled: false
					}
				}
			}
		};

		return new ColumnPanel(
			'Title and chart',
			[
				new StaticParagraphComponent('IQ_FH_CHART_TITLE', 'Overall Fund Health', {}, { paddingLeft: 20, paddingTop: 10 }, ['text-bold']),
				new HighchartsSVGComponent(
					'exposure',
					{ height: 105, paddingLeft: 5, paddingRight: 8, paddingBottom: 5 },
					this.setupChartTabData(data),
					'column',
					customAttributes
				),
				...this.getLegendStatusRows(statusCards),
				...this.getDismissStatusRow(statusCards, this.isDismissSupported())
			],
			{ paddingBottom: 5 }
		);
	}

	private isDismissSupported(): boolean {
		return Boolean(this.enableStatusInteractions && this.supportedFundStatusInteractions
			.find(suppInt => suppInt.id === FundStatusInteractionsConstants.ACCEPT_DISMISS_ID)?.interactions
			.find(int => int.id === FundStatusInteractionsConstants.DISMISS_ID));
	}

	private buildVerticalLineSeparator(itemLength: number, isDismissSupported: boolean): string {
		const verticalSeparatorHeight =
			60 + this.verticalSeparatorHeightMultiplier * (itemLength + Number(isDismissSupported));

		return `
	<svg width="1px" height="${verticalSeparatorHeight}px" viewBox="0 0 1 ${verticalSeparatorHeight}">
		<rect id="Rectangle" x="0" y="0" width="0.5" height="${verticalSeparatorHeight}" fill="#b5b5b5"/>
	</svg>`;
	}

	private getDismissStatusRow(statusCards: Array<FundHealthStatusCardData>, isDismissSupported: boolean): RowPanel[] {
		if (!isDismissSupported) {
			return [];
		}
		const bookTotalNumOfFunds = statusCards.reduce((acc, curr) => curr.totalFunds + curr.dismissCount + acc, 0);
		const dismissedTotalNumOfFunds = statusCards.reduce((acc, curr) => curr.dismissCount + acc, 0);

		const totalPercentage = (dismissedTotalNumOfFunds / bookTotalNumOfFunds * 100).toFixed(2);

		return [new RowPanel('legend-series', [
			new CellPanel([new SquareRenderer('grey', { paddingTop: 1 }, 8)], { proportion: 9 }),
			new CellPanel([new StaticParagraphComponent('DISMISSED_FUNDS_LEGEND_KEY', 'Dismissed')], { proportion: 66 }, Alignment.Horizontal.Left),
			new CellPanel([new StaticParagraphComponent(null, `${totalPercentage}%`)], { proportion: 25 }, Alignment.Horizontal.Right)
		], { marginLeft: 5, marginRight: 8 })];
	}

	private getLegendStatusRows(statusCards: Array<FundHealthStatusCardData>): RowPanel[] {

		const bookTotalNumOfFunds = statusCards.reduce((acc, curr) => curr.totalFunds + curr.dismissCount + acc, 0);

		return statusCards.map(card => {
			const aofPercentage = (card.totalFunds / bookTotalNumOfFunds * 100).toFixed(2);
			return new RowPanel('legend-series', [
				new CellPanel([new SquareRenderer(card.colour.hex, { paddingTop: 1 }, 8)], { proportion: 9 }),
				new CellPanel([new StaticParagraphComponent(<string>card.title.dcrKey, card.title.dcrValue)], { proportion: 66 }, Alignment.Horizontal.Left),
				new CellPanel([new StaticParagraphComponent(null, `${aofPercentage}%`)], { proportion: 25 }, Alignment.Horizontal.Right)
			], { marginLeft: 5, marginRight: 8 });
		});
	}

	private createStatusCardsCells(statusCards: Array<FundHealthStatusCardData>): CellPanel[] {
		const isDismissSupported = this.isDismissSupported();
		const cells = statusCards.map((card, i) => {
			const dismissChildArtefact = isDismissSupported ? [new ColumnPanel(
				'review-status-badge-column',
				[
					new RowPanel('reviewed-status-row', [
						new CellPanel(
							[new IconRenderer('check', 14, 14, card.colour?.reportTextClass[0]?.includes('white') ? DZ_COLOR_BLK_WHITE : DZ_COLOR_BLK_BLACK, { paddingLeft: 2, paddingTop: 3 })],
							{ proportion: 14, backgroundShape: 'circle', borderRadius: 6, backgroundColor: card.colour.hex },
							Alignment.Horizontal.Center
						),
						new CellPanel(
							[
								new StaticParagraphComponent(
									'REVIEWED_STATUS_FUND_COUNT_SUFFIX',
									`{acceptCount} Reviewed`,
									{ acceptCount: card.acceptCount },
									{ proportion: 80 },
									['text-bold', 'text-10']
								)
							],
							{ proportion: 86, marginLeft: 3, marginTop: 2 },
							Alignment.Horizontal.Left)
					], { paddingBottom: 5 }),
					new RowPanel('unreviewed-status-row', [
						new CellPanel(
							[new IconRenderer('circle', 14, 14, card.colour.hex, {})],
							{ proportion: 17 },
							Alignment.Horizontal.Left
						),
						new CellPanel(
							[
								new StaticParagraphComponent(
									'UNREVIEWED_STATUS_FUND_COUNT_SUFFIX',
									`{unreviewedCount} Unreviewed`,
									{ unreviewedCount: card.unreviewedCount },
									{ proportion: 80 },
									['text-bold', 'text-10']
								)
							],
							{ proportion: 83 },
							Alignment.Horizontal.Left)
					], {})

				],
				{ proportion: 63, paddingTop: 13 },
				Alignment.Horizontal.Left
			)] : [];
			const cell = new CellPanel(
				[
					new RowPanel('badge-row',
						[new ColumnPanel(
							'badge-column',
							[
								new StaticParagraphComponent(null, String(card.totalFunds), {}, { paddingBottom: 0 }, [...card.colour.reportTextClass, 'text-14', 'text-bold']),
								new StaticParagraphComponent('STATUS_CARD_FUNDS_TEXT_DCR', 'FUNDS', {}, { paddingTop: 0 }, [...card.colour.reportTextClass, 'text-8'])
							],
							{ proportion: isDismissSupported ? 37 : 100, paddingLeft: 15, paddingRight: 15, paddingTop: 20, paddingBottom: 20, backgroundShape: 'circle', borderRadius: 24, backgroundColor: card.colour.hex }
						),
						...dismissChildArtefact],
						{ paddingLeft: isDismissSupported ? 8 : 0, paddingRight: isDismissSupported ? 0 : 95 }
					),
					new RowPanel('text', [
						new ColumnPanel('status-description', [
							new StaticParagraphComponent(
								<string>card.title.dcrKey,
								card.title.dcrValue,
								{},
								{ paddingLeft: 15, paddingRight: 15, paddingBottom: 5 },
								['text-bold', 'text-10']
							),
							new StaticParagraphComponent(<string>card.subtitle.dcrKey, card.subtitle.dcrValue, {}, { paddingLeft: 15, paddingRight: 15 }, ['text-8'])
						], {}, Alignment.Horizontal.Left)
					])
				],
				{ proportion: 24.5, useFullHeight: true },
				Alignment.Horizontal.Left
			);
			const line = new CellPanel([new ImageRenderer(this.buildVerticalLineSeparator(statusCards.length, isDismissSupported))], { proportion: 0.5, useFullHeight: true, marginTop: 10, marginBottom: 10 }, Alignment.Horizontal.Center);

			return [cell, line];
		});
		return [].concat(...cells);
	}

	private getStatusChanges(componentData: ComponentData): ColumnPanel {
		const asOfDate = componentData.getTabById('as.of.date')?.tableData?.[0]?.cells?.[0]?.[0]?.formattedValue;

		return new ColumnPanel(
			'Status Changes',
			[
				new StaticParagraphComponent('FUND_HEALTH_SUMMARY_STATUS_CHANGE_TITLE', 'Status Changes', {}, { paddingBottom: 6 }, ['text-10', 'text-bold']),
				new StaticParagraphComponent(null, 'As of ' + asOfDate, {}, {}),
				new StaticParagraphComponent('FUND_HEALTH_SUMMARY_STATUS_CHANGE_SUB_TITLE', 'Funds that have changed their status since the previous quarter. Organized into funds that declined and those that improved in status.', {}, {})
			],
			{ paddingLeft: 15, paddingTop: 10, paddingRight: 15 },
			Alignment.Horizontal.Left
		);
	}

	private createStatusChangesCells(componentData: ComponentData): CellPanel[] {
		const tableData = componentData.getTabById('fund.status.changes.individual')?.tableData?.[0];
		const cells = tableData?.cells?.[0].map(cell => {
			const isUpgraded = cell?.config?.id === StatusChangeType.upgradedFunds;
			return {
				statusType: cell?.config?.id,
				fundTypes: cell?.rawValue,
				label: isUpgraded ? 'Upgraded' : 'Downgraded',
				totalFundsCount: Object.values(cell?.rawValue).reduce((sum, count) => sum + count, 0)
			};
		});

		return cells.map(cell => {
			const totalFundsCountDigits = cell?.totalFundsCount.toString().length;
			return new CellPanel(
				[
					new RowPanel(
						'Fund Status',
						[
							new CellPanel(
								[
									new RowPanel(null, [
										new CellPanel([
											new IconRenderer(cell.statusType === StatusChangeType.upgradedFunds ? 'arrow-up' : 'arrow-down', 12, 12, DZ_COLOR_BLK_BLACK, {})
										], { proportion: 20, marginTop: 2 }, Alignment.Horizontal.Left),
										new CellPanel([
											new StaticParagraphComponent(null, cell?.totalFundsCount.toString(), {}, {}, ['text-14', 'text-bold'])
										], { proportion: totalFundsCountDigits * 15 }, Alignment.Horizontal.Left),
										new CellPanel([
											new StaticParagraphComponent('SUMMARY_STATUS_SUBTITLE_SUFFIX', ' FUNDS', {}, {}, ['text-neutral-grey'])
										], { proportion: (100 - 20 - totalFundsCountDigits * 15), marginTop: 4, paddingLeft: 10 }, Alignment.Horizontal.Left)
									], { paddingBottom: 5 }, Alignment.Horizontal.Left),
									new StaticParagraphComponent(`SUMMARY_STATUS_${cell?.statusType.toUpperCase()}_SUBTITLE`, `${cell?.label} in status since last quarter`, {}, {})
								],
								{ proportion: 40, paddingRight: 15 },
								Alignment.Horizontal.Left
							),
							new CellPanel(
								[
									new RowPanel(
										'Fund Details',
										[
											...this.getFundDetailsCells(((cell?.fundTypes as unknown) as FundStatusChange), cell?.statusType)
										],
										{},
										Alignment.Horizontal.Left
									)
								],
								{ proportion: 60, paddingLeft: 15 },
								Alignment.Horizontal.Left
							)
						],
						{ paddingLeft: 15, paddingRight: 15, paddingTop: 15 },
						Alignment.Horizontal.Left
					)
				],
				{ proportion: 37.5, useFullHeight: true },
				Alignment.Horizontal.Left
			);
		});
	}

	private getFundDetailsCells(funds: FundStatusChange, statusType: string): CellPanel[] {
		const entries = FundStatsUtil.sortStatusChanges(funds);
		const fundTypes = entries.map(([status, count]) => {
			let svg: string;
			switch (status) {
				case 'greenRed': {
					svg = statusType === StatusChangeType.downgradedFunds ? 'FUND_STATUS_DOWNGRADE_GREEN_TO_RED' : 'FUND_STATUS_UPGRADE_RED_TO_GREEN';
					break;
				}
				case 'greenYellow': {
					svg = statusType === StatusChangeType.downgradedFunds ? 'FUND_STATUS_DOWNGRADE_GREEN_TO_YELLOW' : 'FUND_STATUS_UPGRADE_YELLOW_TO_GREEN';
					break;
				}
				case 'yellowRed': {
					svg = statusType === StatusChangeType.downgradedFunds ? 'FUND_STATUS_DOWNGRADE_YELLOW_TO_RED' : 'FUND_STATUS_UPGRADE_RED_TO_YELLOW';
				}
			}
			return { status, count, svg };
		});
		const fundCells = fundTypes.filter((fund => fund.count));
		return fundCells.map((fund) => {
			return new CellPanel(
				[
					new ImageRenderer(
						new SvgImage(fund.svg).getStaticContent(),
						{ paddingBottom: 7, paddingLeft: 5 }
					),
					new StaticParagraphComponent('SUMMARY_STATUS_FUND_COUNT_SUFFIX', `{fundCount} Funds`, { fundCount: fund.count }, {})
				],
				{ proportion: 100 / fundCells.length },
				Alignment.Horizontal.Center
			);
		});
	}

	private setupChartTabData(data: ComponentData): TabData {
		let chartTabData;
		if (TabUtil.isDataAvailable(data, 'fund.statuses.chart')) {
			chartTabData = data.getTabById('fund.statuses.chart');
			// setup chart color based on status id
			chartTabData.tableData[0].headersY.forEach(cell => {
				cell.attribute = { ...cell.attribute, colorTheme: StatusColour.get[cell.config.id].hex };
			});
		}
		return chartTabData;
	}
}
